import React from "react";
import { Badge, Tooltip } from "antd";
import { matchPath } from "react-router-dom";
import { appNavigationSections, appRouteRecords } from "./registry";
import { renderNavigationIcon } from "./icon-map";
import { PERMISSION_MATCH } from "./schema";
import { resolveNewFrontendHref } from "./new-frontend-href";

function sortByOrder(left, right) {
    return left.order - right.order;
}

const sectionIconKeyByKey = appNavigationSections.reduce((iconKeys, section) => {
    iconKeys[section.key] = section.iconKey;
    return iconKeys;
}, {});

function getPermissionList(permission) {
    if (!permission) {
        return [];
    }

    return Array.isArray(permission) ? permission : [permission];
}

function hasPermissionAccess(permission, permissionMatch, auth) {
    const permissionList = getPermissionList(permission);

    if (permissionList.length === 0) {
        return true;
    }

    if (!auth?.user) {
        return false;
    }

    if (auth.user.is_superuser) {
        return true;
    }

    if (permissionMatch === PERMISSION_MATCH.ALL) {
        if (typeof auth.hasAllPermissions === "function") {
            return auth.hasAllPermissions(permissionList);
        }

        return permissionList.every((item) => auth.hasPermission(item));
    }

    if (typeof auth.hasAnyPermission === "function") {
        return auth.hasAnyPermission(permissionList);
    }

    return permissionList.some((item) => auth.hasPermission(item));
}

function getMatchScore(pattern) {
    return pattern.replace(/:[^/]+/g, "__param__").length;
}

function findRouteMatch(pathname) {
    let bestMatch = null;

    appRouteRecords.forEach((route) => {
        route.matchPatterns.forEach((pattern) => {
            const matched = matchPath({ path: pattern, end: true }, pathname);

            if (!matched) {
                return;
            }

            const currentMatch = {
                route,
                pattern,
                score: getMatchScore(pattern),
            };

            if (!bestMatch || currentMatch.score > bestMatch.score) {
                bestMatch = currentMatch;
            }
        });
    });

    return bestMatch;
}

function renderBadgeTooltip(detail) {
    if (!detail || typeof detail !== "object") {
        return null;
    }

    const segments = [];
    if (typeof detail.needs_recalculation === "number" && detail.needs_recalculation > 0) {
        segments.push(`需要重算 ${detail.needs_recalculation}`);
    }
    if (typeof detail.failed === "number" && detail.failed > 0) {
        segments.push(`失败 ${detail.failed}`);
    }

    return segments.length > 0 ? segments.join(" · ") : null;
}

function renderMenuLabel(label, badgeKey, badgeTooltipKey, badgeCounts) {
    const badgeCount = badgeKey ? badgeCounts?.[badgeKey] : 0;

    if (!badgeCount) {
        return label;
    }

    const tooltipContent = badgeTooltipKey ? renderBadgeTooltip(badgeCounts?.[badgeTooltipKey]) : null;
    const badgeNode = <Badge className="app-sider-menu-label__badge" count={badgeCount} size="small" />;

    return (
        <span className="app-sider-menu-label">
            <span className="app-sider-menu-label__main">
                <span className="app-sider-menu-label__text">{label}</span>
            </span>
            {tooltipContent ? <Tooltip title={tooltipContent}>{badgeNode}</Tooltip> : badgeNode}
        </span>
    );
}

function getMenuIcon(route) {
    if (route.menu.icon) {
        return route.menu.icon;
    }

    return renderNavigationIcon(route.menu.iconKey, sectionIconKeyByKey[route.menu.sectionKey]);
}

export function hasRouteAccess(route, auth) {
    return hasPermissionAccess(route.routePermission, route.routePermissionMatch, auth);
}

export function findRouteByPath(pathname) {
    return findRouteMatch(pathname)?.route || null;
}

export function getRoutePageLabel(pathname) {
    const matchedRoute = findRouteByPath(pathname);

    if (!matchedRoute) {
        return "";
    }

    if (matchedRoute.pageLabel) {
        return matchedRoute.pageLabel;
    }

    if (matchedRoute.menu?.label) {
        return matchedRoute.menu.label;
    }

    return findRouteByMenuKey(matchedRoute.activeMenuKey)?.menu?.label || "";
}

export function getRouteDocumentLabel(pathname, search = "") {
    const pageLabel = getRoutePageLabel(pathname);
    if (!pageLabel) {
        return "";
    }

    const matchedRoute = findRouteByPath(pathname);
    if (matchedRoute?.key !== "apWorkbench") {
        return pageLabel;
    }

    const batchNumber = new URLSearchParams(search || "").get("batch");
    return batchNumber ? `${batchNumber} - ${pageLabel}` : pageLabel;
}

export function getActiveNavigationState(pathname) {
    const matchedRoute = findRouteByPath(pathname);

    return {
        selectedKeys: matchedRoute?.activeMenuKey ? [matchedRoute.activeMenuKey] : [],
        openSectionKey: matchedRoute?.activeSectionKey || "",
    };
}

export function buildSiderMenuItems({ auth, badgeCounts, onNavigate }) {
    if (auth?.loading) {
        return [];
    }

    const menuRoutesBySection = appNavigationSections.reduce((sectionsMap, section) => {
        sectionsMap[section.key] = [];
        return sectionsMap;
    }, {});

    appRouteRecords
        .filter((route) => route.menu)
        .forEach((route) => {
            if (!hasPermissionAccess(route.menu.permission, route.menu.permissionMatch, auth)) {
                return;
            }

            const sectionRoutes = menuRoutesBySection[route.menu.sectionKey];

            if (!sectionRoutes) {
                return;
            }

            sectionRoutes.push(route);
        });

    return appNavigationSections
        .map((section) => {
            const sectionRoutes = (menuRoutesBySection[section.key] || []).sort((left, right) => sortByOrder(left.menu, right.menu));

            if (sectionRoutes.length === 0) {
                return null;
            }

            return {
                key: section.key,
                icon: section.icon || renderNavigationIcon(section.iconKey),
                label: section.label,
                children: sectionRoutes.map((route) => ({
                    key: route.menu.key,
                    icon: getMenuIcon(route),
                    label: renderMenuLabel(route.menu.label, route.menu.badgeKey, route.menu.badgeTooltipKey, badgeCounts),
                    title: route.menu.label,
                    onClick: () => {
                        if (route.menu.openInNewTab) {
                            window.open(route.menu.navigatePath, "_blank", "noopener");
                            return;
                        }

                        if (route.frontend === "new") {
                            window.location.href = resolveNewFrontendHref(route.menu.navigatePath);
                            return;
                        }

                        onNavigate(route.menu.navigatePath);
                    },
                })),
            };
        })
        .filter(Boolean);
}

function findRouteByMenuKey(menuKey) {
    if (!menuKey) {
        return null;
    }

    return appRouteRecords.find((route) => route.menu?.key === menuKey) || null;
}
